import React, { useEffect, useRef, useState } from "react";
import "../style/css/LandingPage_css.css";
import "../style/css/base.css";
import RegisterLandingPageForm from "./FormForLandingPage";
import {
  initGA,
  trackPageView,
  trackCTAClick,
  trackButtonClick,
  trackFormView,
  trackScrollDepth,
  trackEvent,
} from "../utils/analytics";

const LandingPage: React.FC = () => {
  const formSectionRef = useRef<HTMLElement>(null);
  const [formHasBeenSeen, setFormHasBeenSeen] = useState(false);
  const [scrollDepthsTracked, setScrollDepthsTracked] = useState({
    twentyFive: false,
    fifty: false,
    seventyFive: false,
    ninety: false,
    hundred: false,
  });

  useEffect(() => {
    initGA();
    trackPageView("/landing-page");

    // Set up Intersection Observer for form visibility
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !formHasBeenSeen) {
            // Form is now visible in the viewport
            trackFormView("Registration Form");
            setFormHasBeenSeen(true);

            // We can unobserve after tracking
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.3, // 30% of the form section must be visible
        rootMargin: "0px 0px -50px 0px", // Optional: offset
      },
    );

    // Start observing the form section
    if (formSectionRef.current) {
      observer.observe(formSectionRef.current);
    }

    // Track scroll depth with debouncing
    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight =
          document.documentElement.scrollHeight -
          document.documentElement.clientHeight;
        const scrolled = (scrollTop / scrollHeight) * 100;

        // Track different scroll depths
        if (scrolled >= 25 && !scrollDepthsTracked.twentyFive) {
          trackScrollDepth("25%");
          setScrollDepthsTracked((prev) => ({ ...prev, twentyFive: true }));
        }
        if (scrolled >= 50 && !scrollDepthsTracked.fifty) {
          trackScrollDepth("50%");
          setScrollDepthsTracked((prev) => ({ ...prev, fifty: true }));
        }
        if (scrolled >= 75 && !scrollDepthsTracked.seventyFive) {
          trackScrollDepth("75%");
          setScrollDepthsTracked((prev) => ({ ...prev, seventyFive: true }));
        }
        if (scrolled >= 90 && !scrollDepthsTracked.ninety) {
          trackScrollDepth("90%");
          setScrollDepthsTracked((prev) => ({ ...prev, ninety: true }));
          // Also track form view if not already tracked (as fallback)
          if (!formHasBeenSeen) {
            trackFormView("Registration Form (via 90% scroll)");
            setFormHasBeenSeen(true);
          }
        }
        if (scrolled >= 99 && !scrollDepthsTracked.hundred) {
          trackScrollDepth("100%");
          setScrollDepthsTracked((prev) => ({ ...prev, hundred: true }));
        }
      }, 100); // Debounce for 100ms
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (formSectionRef.current) {
        observer.unobserve(formSectionRef.current);
      }
      clearTimeout(scrollTimeout);
    };
  }, [formHasBeenSeen, scrollDepthsTracked]);

  // Event handlers for tracking
  const handleCTAClick = (ctaName: string) => {
    trackCTAClick(ctaName);
  };

  const handleButtonClick = (buttonName: string) => {
    trackButtonClick(buttonName);
  };
  return (
    <div className="landing-page">
      {/* Header */}
      <header>
        <div className="container">
          <nav className="navbar">
            <div className="logo">
              <div className="logo-img">
                {/* Replace SVG with image */}
                <img
                  src="/logo_resized.jpg"
                  alt="Cara de Agenda Logo"
                  width="60"
                  height="44"
                  style={{ borderRadius: "12px" }}
                />
              </div>
              <div>
                <h3>Cara de Agenda</h3>
                <span className="beta-badge">BETA</span>
              </div>
            </div>
            <div className="navbar-actions">
              {/* New Login Button */}
              <a
                href="/login"
                className="btn btn--secondary"
                onClick={() => handleButtonClick("Iniciar sesión Header")}
              >
                Iniciar sesión
              </a>
              {/* Existing Button */}
              <a href="#registro" className="btn btn--primary">
                Acceso Fundador
              </a>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section - REST OF THE CODE REMAINS EXACTLY THE SAME */}
      <section className="hero">
        <div className="container">
          <div className="fomo-box">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: "8px" }}
            >
              <path
                d="M17.54 8.31a2.46 2.46 0 0 0-3.5 0l-.71.71-.71-.71a2.46 2.46 0 0 0-3.5 3.5l.71.71 3.5 3.5 3.5-3.5.71-.71a2.46 2.46 0 0 0 0-3.5z"
                fill="#FFC107"
              />
              <path
                d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16z"
                fill="#FFC107"
              />
            </svg>
            Solo 10 lugares para Beta Testing (6 meses GRATIS)
          </div>

          <div className="hero-content">
            <div className="hero-text">
              <h1>Deja de ser el secretario de tu propio consultorio</h1>
              <p>
                Tu Google Calendar no persigue a los pacientes para confirmar
                citas.
                <strong> Cara de Agenda sí.</strong> Automatiza tus
                recordatorios por WhatsApp y recupera 10 horas libres a la
                semana.
              </p>

              <div className="cta-group">
                <a
                  href="#registro"
                  className="btn btn--primary btn--large"
                  onClick={() =>
                    handleCTAClick("Quiero mi lugar en la Beta Hero")
                  }
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: "8px" }}
                  >
                    <path
                      d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Quiero mi lugar en la Beta
                </a>
                <a
                  href="#comparativa"
                  className="btn btn--secondary"
                  onClick={() => handleCTAClick("Por qué es mejor que Google")}
                >
                  ¿Por qué es mejor que Google?
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5v14M19 12l-7 7-7-7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              </div>

              <p className="hero-note">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "8px" }}
                >
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="11"
                    rx="2"
                    ry="2"
                    stroke="#9AA7C3"
                    strokeWidth="2"
                  />
                  <path
                    d="M7 11V7a5 5 0 0110 0v4"
                    stroke="#9AA7C3"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Tus datos están seguros. Sin tarjeta de crédito requerida hoy.
              </p>
            </div>

            <div className="hero-image">
              <div className="dashboard-mockup">
                <div className="dashboard-header">
                  <h4 className="dashboard-title">Cara de agenda</h4>
                  <div className="dashboard-nav">
                    <div className="dashboard-nav-item active">Hoy</div>
                    <div className="dashboard-nav-item">Semana</div>
                    <div className="dashboard-nav-item">Mes</div>
                  </div>
                </div>
                <div className="dashboard-content">
                  <div className="appointments-section">
                    <h5 style={{ margin: "0 0 .75rem 0" }}>Próximas Citas</h5>

                    <div className="appointment-item">
                      <div>
                        <div className="appointment-time">10:00 AM</div>
                        <div>María González</div>
                      </div>
                      <span
                        className="appointment-status"
                        style={{
                          background: "#E8F5E8",
                          color: "var(--success)",
                        }}
                      >
                        Confirmada
                      </span>
                    </div>

                    <div className="appointment-item">
                      <div>
                        <div className="appointment-time">11:30 AM</div>
                        <div>Carlos López</div>
                      </div>
                      <span
                        className="appointment-status"
                        style={{
                          background: "#FFF3CD",
                          color: "var(--warning)",
                        }}
                      >
                        Por confirmar
                      </span>
                    </div>

                    <div className="appointment-item">
                      <div>
                        <div className="appointment-time">2:00 PM</div>
                        <div>Ana Martínez</div>
                      </div>
                      <span
                        className="appointment-status"
                        style={{
                          background: "#E8F5E8",
                          color: "var(--success)",
                        }}
                      >
                        Confirmada
                      </span>
                    </div>

                    <div className="appointment-item">
                      <div>
                        <div className="appointment-time">4:15 PM</div>
                        <div>Roberto Díaz</div>
                      </div>
                      <span
                        className="appointment-status"
                        style={{
                          background: "#E8F5E8",
                          color: "var(--success)",
                        }}
                      >
                        Confirmada
                      </span>
                    </div>
                  </div>

                  <div className="stats-section">
                    <div className="stat-card">
                      <div className="stat-value">12</div>
                      <div className="stat-label">Citas esta semana</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">92%</div>
                      <div className="stat-label">Tasa de asistencia</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">5</div>
                      <div className="stat-label">Nuevos pacientes</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section id="comparativa" className="comparison">
        <div className="container">
          <div className="section-header text-center">
            <h2>El costo oculto de lo "Gratis"</h2>
            <p>
              ¿Cuánto vale tu hora de consulta? ¿Por qué la gastas enviando
              mensajes?
            </p>
          </div>

          <div className="comparison-table">
            <div className="table-header">
              <div className="table-col feature-col">Tarea</div>
              <div className="table-col competitor-col">
                Lo que usas hoy (Calendar + WhatsApp)
              </div>
              <div className="table-col our-col">
                Cara de Agenda (Tu empleado virtual)
              </div>
            </div>

            <div className="table-row">
              <div className="table-col feature-col">
                <strong>Confirmar Citas</strong>
              </div>
              <div className="table-col competitor-col">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon-x"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#DC3545"
                    strokeWidth="2"
                  />
                  <path
                    d="M8 8l8 8M8 16l8-8"
                    stroke="#DC3545"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Manual (tú envías el mensaje)
              </div>
              <div className="table-col our-col">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon-check"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#198754"
                    strokeWidth="2"
                  />
                  <path
                    d="M8 12l3 3 5-5"
                    stroke="#198754"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Automático por WhatsApp
              </div>
            </div>

            <div className="table-row">
              <div className="table-col feature-col">
                <strong>Si el paciente olvida</strong>
              </div>
              <div className="table-col competitor-col">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon-x"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#DC3545"
                    strokeWidth="2"
                  />
                  <path
                    d="M8 8l8 8M8 16l8-8"
                    stroke="#DC3545"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Pierdes dinero (Inasistencia)
              </div>
              <div className="table-col our-col">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon-check"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#198754"
                    strokeWidth="2"
                  />
                  <path
                    d="M8 12l3 3 5-5"
                    stroke="#198754"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                El sistema recuerda amablemente
              </div>
            </div>

            <div className="table-row">
              <div className="table-col feature-col">
                <strong>Agendar Cita</strong>
              </div>
              <div className="table-col competitor-col">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon-x"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#DC3545"
                    strokeWidth="2"
                  />
                  <path
                    d="M8 8l8 8M8 16l8-8"
                    stroke="#DC3545"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Ping-pong de mensajes ("¿tienes a las 5?")
              </div>
              <div className="table-col our-col">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon-check"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#198754"
                    strokeWidth="2"
                  />
                  <path
                    d="M8 12l3 3 5-5"
                    stroke="#198754"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Paciente elige en tu web 24/7
              </div>
            </div>

            <div className="table-row">
              <div className="table-col feature-col">
                <strong>Expediente Clínico</strong>
              </div>
              <div className="table-col competitor-col">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon-x"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#DC3545"
                    strokeWidth="2"
                  />
                  <path
                    d="M8 8l8 8M8 16l8-8"
                    stroke="#DC3545"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                En papel o carpeta separada
              </div>
              <div className="table-col our-col">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon-check"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#198754"
                    strokeWidth="2"
                  />
                  <path
                    d="M8 12l3 3 5-5"
                    stroke="#198754"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Integrado a la cita
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <div className="section-header text-center">
            <h2>Tu consultorio en piloto automático</h2>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
                    stroke="var(--color-accent)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3>WhatsApp para tus Pacientes</h3>
              <p>
                Ellos no bajan ninguna App. Reciben recordatorios, confirman o
                reprograman directo en el WhatsApp que ya usan.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                    stroke="var(--color-accent)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3>Privacidad para Ti (Telegram)</h3>
              <p>
                <strong>Separamos tu vida personal del trabajo.</strong> Recibe
                notificaciones de nuevas citas y agenda del día en Telegram, sin
                mezclarlo con tus chats de amigos en WhatsApp.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    stroke="var(--color-accent)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 2v6h6M16 13H8M16 17H8M10 9H8"
                    stroke="var(--color-accent)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3>Expediente Integrado</h3>
              <p>
                Olvídate de buscar la ficha en el archivero. Al abrir la cita,
                tienes todo el historial del paciente listo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing">
        <div className="container">
          <div className="offer-card">
            <div className="offer-badge">OFERTA DE PRE-LANZAMIENTO</div>
            <div className="offer-content">
              <h2>Asegura el precio más bajo de la historia</h2>
              <p>
                El software costará $699/mes cuando lancemos públicamente. Si te
                unes a la lista hoy, congelas el precio de fundador.
              </p>

              <div className="price-comparison">
                <div className="price-old">$699</div>
                <div className="price-new">
                  $399 <span className="price-period">/mes</span>
                </div>
              </div>

              <div className="offer-bonus">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "8px" }}
                >
                  <path
                    d="M12 1v22M5 8l7-7 7 7M5 16l7 7 7-7"
                    stroke="var(--color-accent)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                BONUS: Los primeros 15 seleccionados obtienen 6 MESES GRATIS
                como Beta Testers.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form - Ahora con el formulario completo */}
      <section id="registro" className="registration">
        <div className="container">
          <div className="section-header text-center">
            <h2>Aparta tu lugar ahora</h2>
            <p>
              Estamos finalizando las integraciones. Sé el primero en entrar.
            </p>
          </div>

          <div className="form-container-wrapper">
            <RegisterLandingPageForm
              onFormSubmit={() => {
                trackEvent("Form", "Submit", "Landing Page Form");
              }}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="container">
          <p>
            &copy; 2026 Cara de Agenda. Hecho en México para profesionales de la
            salud.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
